import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetResourceByIdQuery } from "@generated";
import { ResourceForm } from "../ResourceForm/ResourceForm";
import { useResourceMutations } from "../api";
import { emptyResourceForm } from "../types";
import type { ResourceFormValues } from "../types";
import type { ProjectVariable } from "../../ProfessionalServices/common/types/ProjectVariable";

const FORM_ID = "resource-form";

// The resource's user-defined-fields come back as the account's resource
// definitions with this resource's values merged in. Normalize to ProjectVariable
// so UserDefinedFieldCollection can render them.
const toProjectVariables = (fields: any[]): ProjectVariable[] =>
  (fields ?? []).map((v: any) => ({
    id: v.uuid ?? v.name,
    name: v.name,
    label: v.label ?? "",
    required: v.required ?? false,
    "variable-type": v["variable-type"] ?? v.variable_type,
    variable_type: v.variable_type,
    select_options: v.select_options ?? [],
    "select-options": v["select-options"] ?? [],
    minimum: v.minimum,
    maximum: v.maximum,
    value: v.value ?? "",
  }));

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useResourceMutations();
  const { data, isLoading, refetch } = useV2GetResourceByIdQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<ResourceFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<ResourceFormValues>(() => {
    const attrs: any = data?.data?.attributes;
    if (!attrs) return emptyResourceForm;
    return {
      name: attrs.name ?? "",
      externalName: attrs["external-name"] ?? "",
      description: attrs.description ?? "",
      default: !!attrs.default,
      userDefinedFields: toProjectVariables(attrs["user-defined-fields"]),
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: ResourceFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update resource", err);
      setToast({ variant: "danger", text: "Could not save resource." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Resource"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <ResourceForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
